import type {
    MediaAudioDeviceConstraints,
    MediaAudioInputDevice,
    MediaAudioInputQuery,
    MediaOutputDevice,
    MediaVideoDeviceConstraints,
    MediaVideoInputDevice,
    MediaVideoInputQuery,
} from '@yandex-360/telemost-sdk/device-manager';

type RequestedDeviceId = Maybe<string | boolean | null>;

const DEFAULT_DEVICE_ID = 'default';

export function getRequestedVideoInputDeviceId(videoEnabled: boolean, deviceId: string): Maybe<string> {
    return videoEnabled ? deviceId || DEFAULT_DEVICE_ID : undefined;
}

function findDeviceById<TDevice extends { id: string }>(
    deviceId: Maybe<string>,
    devices: Array<TDevice>
): Maybe<TDevice> {
    if (deviceId === undefined || !devices.length) {
        return;
    }

    return (
        devices.find((device) => device.id === deviceId) ||
        devices.find((device) => device.id === DEFAULT_DEVICE_ID) ||
        devices[0]
    );
}

function findVideoInputDeviceById(
    deviceId: string,
    devices: Array<MediaVideoInputDevice>
): Maybe<MediaVideoInputDevice> {
    if (deviceId === DEFAULT_DEVICE_ID) {
        return devices.find(({ id }) => id === DEFAULT_DEVICE_ID);
    }

    return findDeviceById(deviceId, devices);
}

function createAudioInputDeviceQuery(constraints?: MediaAudioDeviceConstraints): MediaAudioInputQuery {
    return {
        kind: 'audioinput',
        constraints,
    };
}

function createVideoInputDeviceQuery(constraints?: MediaVideoDeviceConstraints): MediaVideoInputQuery {
    return {
        kind: 'videoinput',
        constraints,
    };
}

export function getAudioInputDeviceOrQueryForDeviceManager(
    devices: Array<MediaAudioInputDevice>,
    deviceId: RequestedDeviceId,
    constraints?: MediaAudioDeviceConstraints
): Maybe<MediaAudioInputDevice | MediaAudioInputQuery> {
    if (typeof deviceId === 'string') {
        const device = findDeviceById(deviceId, devices);

        return device && constraints ? { ...device, constraints } : device || createAudioInputDeviceQuery(constraints);
    }

    return deviceId ? createAudioInputDeviceQuery(constraints) : undefined;
}

export function getVideoInputDeviceOrQueryForDeviceManager(
    devices: Array<MediaVideoInputDevice>,
    deviceId: RequestedDeviceId,
    constraints?: MediaVideoDeviceConstraints
): Maybe<MediaVideoInputDevice | MediaVideoInputQuery> {
    if (typeof deviceId === 'string') {
        const device = findVideoInputDeviceById(deviceId, devices);

        return device && constraints ? { ...device, constraints } : device || createVideoInputDeviceQuery(constraints);
    }

    return deviceId ? createVideoInputDeviceQuery(constraints) : undefined;
}

export function getOutputDeviceForDeviceManager(
    devices: Array<MediaOutputDevice>,
    deviceId: RequestedDeviceId
): Maybe<MediaOutputDevice> {
    if (typeof deviceId === 'string') {
        return findDeviceById(deviceId, devices);
    }

    return deviceId ? devices.find((device) => device.id === DEFAULT_DEVICE_ID) || devices[0] : undefined;
}
